import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Menu, X, ChevronDown, Sparkles, Tv2, Search, BarChart3, ArrowRight } from "lucide-react";

export const serviceCategories = [
  {
    title: "Traditional Media",
    badge: "Broadcast & Real-World",
    icon: Tv2,
    color: "text-amber-400",
    headerColor: "text-amber-300 border-amber-500/30",
    items: [
      { label: "Television", href: "/service/television-advertising-services", isRoute: true },
      { label: "Radio", href: "/service/radio-advertising-services", isRoute: true },
      { label: "Print", href: "/dev/service/print-advertisements-services", isRoute: true },
      { label: "Cinema Advertising", href: "/dev/service/cinema-advertising-services", isRoute: true },
      { label: "Outdoor (OOH) & Transit Media", href: "/services/outdoor-advertising-services", isRoute: true },
    ],
  },
  {
    title: "Digital Media",
    badge: "Performance & Tech",
    icon: Search,
    color: "text-purple-400",
    headerColor: "text-purple-300 border-purple-500/30",
    items: [
      { label: "Performance Marketing (Google, Meta, LinkedIn Ads)", href: "/services/performance-marketing-services", isRoute: true },
      { label: "Display, Native, Rich Media Campaigns", href: "/services/display-native-ads-services", isRoute: true },
      { label: "OTT Advertising", href: "/services/ott-advertising-services", isRoute: true },
      { label: "App Advertising", href: "/services/app-advertising-services", isRoute: true },
      { label: "Programmatic Advertising", href: "/services/programmatic-advertising-services", isRoute: true },
      { label: "SEO", href: "/services/seo-services", isRoute: true },
      { label: "Online Reputation Management (ORM)", href: "/services/orm-services", isRoute: true },
      { label: "Social Media Marketing", href: "/services/social-media-marketing-services", isRoute: true },
      { label: "AI Marketing", href: "/services/ai-marketing-services", isRoute: true },
      { label: "Website Design, UI/UX Design", href: "/services/web-development-services", isRoute: true },
      { label: "Web Development & Landing Pages", href: "/services/web-development-services", isRoute: true },
      { label: "Marketing Automation", href: "/services/email-marketing-services", isRoute: true },
      { label: "CRM Marketing", href: "/services/email-marketing-services", isRoute: true },
      { label: "Email Marketing", href: "/services/email-marketing-services", isRoute: true },
      { label: "Analytics", href: "/services/analytics-services", isRoute: true },
      { label: "Conversion Optimisation (CRO)", href: "/services/cro-services", isRoute: true },
      { label: "Data Intelligence", href: "/services/data-intelligence-services", isRoute: true },
    ],
  },
  {
    title: "Branding & Creatives",
    badge: "Creative & Identity",
    icon: Sparkles,
    color: "text-pink-400",
    headerColor: "text-pink-300 border-pink-500/30",
    items: [
      { label: "Branding & Strategy", href: "/services/branding-creatives-and-brand-strategy-services", isRoute: true },
      { label: "Logo & Identity", href: "/services/branding-creatives-and-logo-identity-services", isRoute: true },
      { label: "Motion & AI Video", href: "/services/branding-creatives-and-motion-ai-video-services", isRoute: true },
      { label: "Video Production", href: "/services/branding-creatives-and-video-production-services", isRoute: true },
      { label: "Photography & Copywriting", href: "/services/branding-creatives-and-photography-copywriting-services", isRoute: true },
      { label: "Campaign Design", href: "/services/branding-creatives-and-campaign-design-services", isRoute: true },
      { label: "Corporate Office Branding", href: "/services/branding-creatives-and-office-branding-services", isRoute: true },
    ],
  },
  {
    title: "Research & Consulting",
    badge: "Audits & Strategy",
    icon: BarChart3,
    color: "text-emerald-400",
    headerColor: "text-emerald-300 border-emerald-500/30",
    items: [
      { label: "Research & Consulting", href: "/services/research-consulting-services", isRoute: true },
      { label: "Media Audit", href: "/services/research-consulting-and-media-audit-services", isRoute: true },
      { label: "Brand Audit", href: "/services/research-consulting-and-brand-audit-services", isRoute: true },
      { label: "Campaign Audit", href: "/services/research-consulting-and-campaign-audit-services", isRoute: true },
    ],
  },
];

const mainLinks = [
  { href: "/about-us", label: "About Us", isRoute: true },
  { href: "/services", label: "Services", isRoute: true, hasDropdown: true },
  { href: "/work", label: "Industries", isRoute: true },
  { href: "/process", label: "Process", isRoute: true },
  { href: "/contact-us", label: "Contact", isRoute: true },
];

export function SiteNav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleMouseEnterDropdown = () => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setDropdownOpen(true);
  };

  const handleMouseLeaveDropdown = () => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    dropdownTimeoutRef.current = setTimeout(() => {
      setDropdownOpen(false);
    }, 450);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="container-x relative">
        <nav
          className={`flex items-center justify-between rounded-full px-4 md:px-6 py-3 w-full mx-auto glass-strong ring-glow transition-all duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)] ${
            scrolled ? "max-w-[1216px]" : "max-w-[850px]"
          }`}
        >
          <Link to="/" className="flex items-center gap-2 group shrink-0">
            <img 
              src="/images/kmwa_logo.png" 
              alt="KMWA" 
              className="h-10 w-auto object-contain group-hover:scale-105 transition-transform duration-300"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {mainLinks.map((l) => (
              l.hasDropdown ? (
                <div
                  key={l.href}
                  className="relative group/nav py-1"
                  onMouseEnter={handleMouseEnterDropdown}
                  onMouseLeave={handleMouseLeaveDropdown}
                >
                  <Link
                    to="/services"
                    onClick={(e) => {
                      // On click, allow navigation or toggle if needed
                      if (window.innerWidth < 1024) {
                        e.preventDefault();
                        setDropdownOpen((prev) => !prev);
                      } else {
                        setDropdownOpen(false);
                      }
                    }}
                    onMouseEnter={handleMouseEnterDropdown}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  >
                    <span>{l.label}</span>
                    <ChevronDown
                      className={`h-3.5 w-3.5 text-muted-foreground transition-transform duration-300 ${
                        dropdownOpen ? "rotate-180 text-[var(--glow-orange)]" : "group-hover/nav:text-foreground"
                      }`}
                    />
                  </Link>
                </div>
              ) : l.isRoute ? (
                <Link
                  key={l.href}
                  to={l.href as any}
                  className="px-3.5 py-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  className="px-3.5 py-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
                >
                  {l.label}
                </a>
              )
            ))}
          </div>

          <Link to="/contact-us" className="hidden md:inline-flex btn-hero !py-2 !px-5 !text-sm">
            Start Project
          </Link>

          <button
            aria-label="Toggle menu"
            className="md:hidden p-2 text-foreground"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </nav>

        {/* Mega Dropdown Menu (Direct Child of container-x for Perfect Full-Width Centering & Gapless Hover) */}
        <div
          onMouseEnter={handleMouseEnterDropdown}
          onMouseLeave={handleMouseLeaveDropdown}
          className={`absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[1140px] max-w-[96vw] z-[100] transition-all duration-200 ease-out ${
            dropdownOpen
              ? "opacity-100 visible pointer-events-auto translate-y-0"
              : "opacity-0 invisible pointer-events-none -translate-y-2"
          }`}
        >
          {/* Seamless Invisible Hover Bridge connecting navbar to menu */}
          <div className="absolute -top-6 inset-x-0 h-8 bg-transparent pointer-events-auto" />

          <div className="rounded-3xl border border-white/20 p-7 shadow-[0_25px_80px_rgba(0,0,0,0.98)] bg-[#0a0a14] grid grid-cols-12 gap-6 relative overflow-hidden">
            <div className="glow-orb -top-20 -right-20 h-64 w-64 bg-[var(--glow-orange)] opacity-10" />

              {/* Col 1: Traditional Media & Research (Span 3) */}
              <div className="col-span-3 space-y-6">
                {/* Traditional Media */}
                <div>
                  <div className="flex items-center gap-2 mb-3 pb-1.5 border-b border-amber-500/30">
                    <Tv2 className="h-4.5 w-4.5 text-amber-400 shrink-0" />
                    <span className="text-sm font-mono font-bold uppercase tracking-wider text-amber-300">
                      Traditional Media
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {serviceCategories[0].items.map((item) => (
                      <Link
                        key={item.label}
                        to={item.href as any}
                        onClick={() => setDropdownOpen(false)}
                        className="block text-[13.5px] font-semibold text-slate-200 hover:text-white hover:translate-x-1 transition-all py-1.5 px-3 rounded-xl hover:bg-white/10"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Research & Consulting */}
                <div>
                  <Link
                    to="/services/research-consulting-services"
                    onClick={() => setDropdownOpen(false)}
                    className="flex items-center gap-2 mb-3 pb-1.5 border-b border-emerald-500/30 hover:border-emerald-400 group/cat cursor-pointer"
                  >
                    <BarChart3 className="h-4.5 w-4.5 text-emerald-400 shrink-0 group-hover/cat:scale-110 transition-transform" />
                    <span className="text-sm font-mono font-bold uppercase tracking-wider text-emerald-300 group-hover/cat:text-white transition-colors">
                      Research & Consulting
                    </span>
                  </Link>
                  <div className="space-y-1.5">
                    {serviceCategories[3].items.map((item) => (
                      <Link
                        key={item.label}
                        to={item.href as any}
                        onClick={() => setDropdownOpen(false)}
                        className="block text-[13.5px] font-semibold text-slate-200 hover:text-white hover:translate-x-1 transition-all py-1.5 px-3 rounded-xl hover:bg-white/10"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Col 2 & 3: Digital Media (Span 6 - Grid 2 cols) */}
              <div className="col-span-6 border-x border-white/10 px-5 space-y-3">
                <div className="flex items-center gap-2 pb-1.5 border-b border-purple-500/30 mb-3">
                  <Search className="h-4.5 w-4.5 text-purple-400 shrink-0" />
                  <span className="text-sm font-mono font-bold uppercase tracking-wider text-purple-300">
                    Digital Media
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                  {serviceCategories[1].items.map((item) => (
                    <Link
                      key={item.label}
                      to={item.href as any}
                      onClick={() => setDropdownOpen(false)}
                      className="block text-[13.5px] font-semibold text-slate-200 hover:text-white hover:translate-x-1 transition-all py-1.5 px-3 rounded-xl hover:bg-white/10 leading-snug"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Col 4: Branding & Creatives (Span 3) */}
              <div className="col-span-3 space-y-3">
                <Link
                  to="/services/branding-creatives-and-brand-strategy-services"
                  onClick={() => setDropdownOpen(false)}
                  className="flex items-center gap-2 mb-3 pb-1.5 border-b border-pink-500/30 hover:border-pink-400 group/cat cursor-pointer"
                >
                  <Sparkles className="h-4.5 w-4.5 text-pink-400 shrink-0 group-hover/cat:scale-110 transition-transform" />
                  <span className="text-sm font-mono font-bold uppercase tracking-wider text-pink-300 group-hover/cat:text-white transition-colors">
                    Branding & Creatives
                  </span>
                </Link>
                <div className="space-y-1.5">
                  {serviceCategories[2].items.map((item) => (
                    <Link
                      key={item.label}
                      to={item.href as any}
                      onClick={() => setDropdownOpen(false)}
                      className="block text-[13.5px] font-semibold text-slate-200 hover:text-white hover:translate-x-1 transition-all py-1.5 px-3 rounded-xl hover:bg-white/10 leading-snug"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Footer Bar */}
              <div className="col-span-12 pt-3.5 border-t border-white/10 flex items-center justify-between bg-white/[0.02] -mx-7 -mb-7 p-4 px-7">
                <span className="text-xs font-mono font-semibold text-slate-300">
                  Full-Funnel Omnichannel Advertising & Growth Intelligence
                </span>
                <Link
                  to="/services"
                  onClick={() => setDropdownOpen(false)}
                  className="text-sm font-mono font-bold text-[var(--glow-orange)] hover:text-white inline-flex items-center gap-1.5 transition"
                >
                  All Services Overview & Detailed Breakdown
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>

        {/* Mobile Navigation Menu */}
        {open && (
          <div className="md:hidden mt-3 glass-strong rounded-3xl p-4 animate-[fade-up_.3s_ease] max-h-[85vh] overflow-y-auto bg-[#0a0a14] border border-white/20">
            <div className="flex flex-col gap-2">
              <Link
                to="/about-us"
                onClick={() => setOpen(false)}
                className="px-4 py-2.5 text-sm font-semibold text-slate-200 hover:text-white rounded-2xl hover:bg-white/10 transition"
              >
                About Us
              </Link>

              {/* Mobile Services Accordion */}
              <div>
                <button
                  onClick={() => setMobileServicesOpen((prev) => !prev)}
                  className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-semibold text-white rounded-2xl bg-white/5 hover:bg-white/10 transition"
                >
                  <span>Services</span>
                  <ChevronDown
                    className={`h-4 w-4 text-muted-foreground transition-transform duration-300 ${
                      mobileServicesOpen ? "rotate-180 text-[var(--glow-orange)]" : ""
                    }`}
                  />
                </button>

                {mobileServicesOpen && (
                  <div className="pl-3 pt-2 pb-3 space-y-4 border-l border-white/15 ml-4 mt-2">
                    <Link
                      to="/services"
                      onClick={() => setOpen(false)}
                      className="block text-sm font-mono font-bold text-[var(--glow-orange)] hover:text-white uppercase tracking-wider mb-3"
                    >
                      → All Services Overview
                    </Link>

                    {serviceCategories.map((cat) => (
                      <div key={cat.title} className="space-y-2">
                        <span className={`text-xs font-mono font-bold uppercase tracking-wider block ${cat.color}`}>
                          {cat.title}
                        </span>
                        <div className="space-y-1.5 pl-2">
                          {cat.items.map((item) => (
                            item.isRoute ? (
                              <Link
                                key={item.label}
                                to={item.href as any}
                                onClick={() => setOpen(false)}
                                className="block text-sm font-semibold text-slate-200 hover:text-white py-1"
                              >
                                {item.label}
                              </Link>
                            ) : (
                              <a
                                key={item.label}
                                href={item.href}
                                onClick={() => setOpen(false)}
                                className="block text-sm font-semibold text-slate-200 hover:text-white py-1"
                              >
                                {item.label}
                              </a>
                            )
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/work"
                onClick={() => setOpen(false)}
                className="px-4 py-2.5 text-sm font-semibold text-slate-200 hover:text-white rounded-2xl hover:bg-white/10 transition"
              >
                Industries
              </Link>

              <Link
                to="/process"
                onClick={() => setOpen(false)}
                className="px-4 py-2.5 text-sm font-semibold text-slate-200 hover:text-white rounded-2xl hover:bg-white/10 transition"
              >
                Process
              </Link>

              <Link
                to="/contact-us"
                onClick={() => setOpen(false)}
                className="px-4 py-2.5 text-sm font-semibold text-slate-200 hover:text-white rounded-2xl hover:bg-white/10 transition"
              >
                Contact
              </Link>

              <Link
                to="/contact-us"
                onClick={() => setOpen(false)}
                className="btn-hero text-center mt-2"
              >
                Start Project
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

export function HeroOrb() {
  const ref = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsMobile(window.innerWidth < 768);
      const handleResize = () => {
        setIsMobile(window.innerWidth < 768);
      };
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    let tx = 0, ty = 0, cx = 0, cy = 0;
    const onMove = (e: MouseEvent) => {
      const r = el.getBoundingClientRect();
      tx = ((e.clientX - r.left - r.width / 2) / r.width) * 20;
      ty = ((e.clientY - r.top - r.height / 2) / r.height) * 20;
    };
    const loop = () => {
      if (window.scrollY < 900) {
        cx += (tx - cx) * 0.08;
        cy += (ty - cy) * 0.08;
        el.style.transform = `translate3d(${cx}px, ${cy}px, 0)`;
      }
      raf = requestAnimationFrame(loop);
    };
    window.addEventListener("mousemove", onMove);
    raf = requestAnimationFrame(loop);
    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      ref={ref}
      className="relative mx-auto h-[280px] w-[280px] sm:h-[360px] sm:w-[360px] md:h-[420px] md:w-[420px] lg:h-[460px] lg:w-[460px] xl:h-[520px] xl:w-[520px]"
      aria-hidden
    >
      {/* Glow halo */}
      <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_50%_50%,color-mix(in_oklab,var(--glow-purple)_40%,transparent),transparent_60%)] animate-pulse-glow" />
      <div className="absolute -inset-10 rounded-full bg-[radial-gradient(circle_at_50%_50%,color-mix(in_oklab,var(--glow-orange)_25%,transparent),transparent_70%)]" />

      {/* Wireframe sphere */}
      <svg viewBox="0 0 200 200" className="absolute inset-0 h-full w-full animate-spin-slow">
        <defs>
          <linearGradient id="strokeG" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="oklch(0.72 0.19 50)" />
            <stop offset="60%" stopColor="oklch(0.62 0.22 300)" />
            <stop offset="100%" stopColor="oklch(0.78 0.14 220)" />
          </linearGradient>
        </defs>
        <g fill="none" stroke="url(#strokeG)" strokeWidth="0.4" opacity="0.85">
          <circle cx="100" cy="100" r="78" />
          <ellipse cx="100" cy="100" rx="78" ry="30" />
          <ellipse cx="100" cy="100" rx="78" ry="55" />
          <ellipse cx="100" cy="100" rx="30" ry="78" />
          <ellipse cx="100" cy="100" rx="55" ry="78" />
          <ellipse cx="100" cy="100" rx="78" ry="20" transform="rotate(30 100 100)" />
          <ellipse cx="100" cy="100" rx="78" ry="20" transform="rotate(-30 100 100)" />
          <ellipse cx="100" cy="100" rx="78" ry="20" transform="rotate(60 100 100)" />
          <ellipse cx="100" cy="100" rx="78" ry="20" transform="rotate(-60 100 100)" />
        </g>
      </svg>

      {/* Inner core */}
      <div className="absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--gradient-aurora)] blur-md opacity-90 animate-pulse-glow" />
      <div className="absolute left-1/2 top-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/90 shadow-[0_0_60px_10px_oklch(0.72_0.19_50)]" />

      {/* Orbiting tags wrapper */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Mobile View */}
        <div className="block sm:hidden">
          {mobileServices.map((service, idx) => {
            const delaySec = -((36 / mobileServices.length) * idx).toFixed(2);
            return (
              <div
                key={`mobile-${service.label}`}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto"
              >
                <FloatingTag
                  label={service.label}
                  delay={`${delaySec}s`}
                  duration="36s"
                  tone={service.tone}
                  radiusMult={1.45}
                />
              </div>
            );
          })}
        </div>

        {/* Desktop View */}
        <div className="hidden sm:block">
          {/* Ring 1 */}
          {ringInner.map((service, idx) => {
            const delaySec = -((30 / ringInner.length) * idx).toFixed(2);
            return (
              <div
                key={service.label}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto"
              >
                <FloatingTag
                  label={service.label}
                  delay={`${delaySec}s`}
                  duration="30s"
                  tone={service.tone}
                  radiusMult={0.60}
                />
              </div>
            );
          })}

          {/* Ring 2 */}
          {ringMiddle.map((service, idx) => {
            const delaySec = -((38 / ringMiddle.length) * idx).toFixed(2);
            return (
              <div
                key={service.label}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto"
              >
                <FloatingTag
                  label={service.label}
                  delay={`${delaySec}s`}
                  duration="38s"
                  reverse
                  tone={service.tone}
                  radiusMult={1.02}
                />
              </div>
            );
          })}

          {/* Ring 3 */}
          {ringOuter.map((service, idx) => {
            const delaySec = -((46 / ringOuter.length) * idx).toFixed(2);
            return (
              <div
                key={service.label}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto"
              >
                <FloatingTag
                  label={service.label}
                  delay={`${delaySec}s`}
                  duration="46s"
                  tone={service.tone}
                  radiusMult={1.45}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const mobileServices = [
  { label: "Media Buying", tone: "orange" },
  { label: "Performance Marketing", tone: "cyan" },
  { label: "Brand Strategy", tone: "purple" },
  { label: "Programmatic", tone: "cyan" },
  { label: "SEO", tone: "orange" },
] as const;

const ringInner = [
  { label: "SEO", tone: "orange" },
  { label: "PPC", tone: "cyan" },
  { label: "Creative", tone: "orange" },
  { label: "Meta Ads", tone: "purple" },
  { label: "Google Ads", tone: "cyan" },
] as const;

const ringMiddle = [
  { label: "Brand Strategy", tone: "purple" },
  { label: "Media Buying", tone: "orange" },
  { label: "Social Media", tone: "purple" },
  { label: "Programmatic", tone: "cyan" },
  { label: "Digital PR", tone: "orange" },
  { label: "YouTube Ads", tone: "purple" },
] as const;

const ringOuter = [
  { label: "Performance Marketing", tone: "cyan" },
  { label: "Media Planning", tone: "orange" },
  { label: "Digital Marketing", tone: "cyan" },
  { label: "Content Marketing", tone: "purple" },
  { label: "Influencer Marketing", tone: "orange" },
  { label: "OTT Advertising", tone: "cyan" },
  { label: "Connected TV (CTV)", tone: "purple" },
  { label: "Display Advertising", tone: "cyan" },
] as const;

function FloatingTag({
  label,
  tone = "orange",
  delay = "0s",
  duration = "36s",
  reverse = false,
  radiusMult = 1,
}: {
  label: string;
  tone?: "orange" | "purple" | "cyan";
  delay?: string;
  duration?: string;
  reverse?: boolean;
  radiusMult?: number;
}) {
  const color =
    tone === "orange"
      ? "var(--glow-orange)"
      : tone === "purple"
        ? "var(--glow-purple)"
        : "var(--glow-cyan)";
  return (
    <div
      className="tag-orbit animate-tag-orbit rounded-full px-2 py-0.5 sm:px-2.5 sm:py-1 bg-[#09090f]/95 border border-white/20 text-[9.5px] sm:text-[11px] font-semibold cursor-pointer transition-all duration-300 hover:scale-115 hover:z-50 hover:border-white/40 hover:shadow-[0_0_25px_var(--hover-color)] whitespace-nowrap shadow-md backdrop-blur-md text-white/90"
      style={{
        boxShadow: `0 0 15px -4px ${color}`,
        animationDelay: delay,
        animationDuration: duration,
        animationDirection: reverse ? "reverse" : "normal",
        // @ts-ignore
        "--r-mult": radiusMult,
        // @ts-ignore
        "--hover-color": color,
      }}
    >
      <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: color }} />
      {label}
    </div>
  );
}
